import { Typography, useMediaQuery } from '@mui/material';
import moment from 'moment';
import {
	BooleanInput,
	Datagrid,
	FunctionField,
	List,
	NumberField,
	ReferenceField,
	ReferenceInput,
	SelectInput,
	SimpleList,
	TextField,
	TextInput,
	useEditContext,
	useRecordContext,
	useResourceContext,
} from 'react-admin';
import { IoIosNotificationsOutline } from 'react-icons/io';
import { RiAdminLine } from 'react-icons/ri';
import MyPagination from '../MyPagination';
import { useEffect } from 'react';

const MemberList = () => {
	const isSmall = useMediaQuery((theme: any) => theme.breakpoints.down('sm'));
	const context = useResourceContext({ resource: 'member' });

	

	return (
		<>
			<List
				filterDefaultValues={{ active: true }}
				perPage={50}
				filters={[
					<BooleanInput source="active" alwaysOn defaultChecked label="משתמשים פעילים" />,
					<BooleanInput source="is_new" defaultChecked label="משתמשים חדשים" />,
					<ReferenceInput source="company" reference="company" perPage={1000}>
						<SelectInput optionText={'english_name'} />
					</ReferenceInput>,
					<TextInput source="email" />,
					// GH-728 (Terra): search by phone in any format (+972…, 05…, with dashes). The data provider
					// normalises it to the last 7 digits and only sends it once ≥5 digits were typed.
					<TextInput source="phone" label="טלפון" helperText="מ-5 ספרות, בכל פורמט" />,
					<TextInput source="first_name" />,
					<TextInput source="last_name" />,
					<SelectInput
						emptyText={'הכל'}
						source="type"
						choices={[
							{ name: 'מנוי לידה', id: 'newborn' },
							{ name: 'התנסות', id: 'trial' },
							{ name: 'מומחה', id: 'expert' },
						]}
					/>,
				]}
				pagination={<MyPagination />}
			>
				{isSmall ? (
					<SimpleList
						resource={context}
						sx={{ textAlign: 'right' }}
						style={{ textAlign: 'right' }}
						primaryText={(record) => (
							<Typography textAlign="right" fontWeight={'600'}>
								{`${record.email}`}
							</Typography>
						)}
						secondaryText={(record) => <Typography textAlign="left">{record.type}</Typography>}
					/>
				) : (
					<Datagrid
						rowClick="show"
						bulkActionButtons={false}
						dir="rtl"
						sx={{
							'& .column-first_name,.column-last_name, .column-email, .column-phone': { maxWidth: 250 },
						}}
					>
						<FunctionField
							render={(record: any) => {
								const userTypesColors = {
									newborn: 'bg-blue-200',
									trial: 'bg-yellow-200',
									expert: 'bg-green-200',
									hr: 'bg-red-200',
									familyMember: 'bg-orange-200',
								};
								const userTypesNames = {
									newborn: 'מנוי לידה',
									trial: 'התנסות',
									expert: 'מומחה',
									hr: 'HR',
									familyMember: 'בן/ת משפחה',
								};
								const userType: keyof typeof userTypesColors = record.type?.includes('newborn') ? (
									'newborn'
								) : record?.type === 'expert' ? (
									'expert'
								) : record?.type === 'trial' ? (
									'trial'
								) : record.hr ? (
									'hr'
								) : record.invited_by ? (
									'familyMember'
								) : record.role?.includes('admin') ? (
									<span dir="ltr" className="flex flex-row items-center gap-0.5">
										<RiAdminLine />
										<span>{record.role}</span>
									</span>
								) : record.role !== 'user' ? (
									record.role
								) : (
									''
								);

								return (
									<span className="flex items-start flex-col">
										<div className="flex flex-row items-center gap-1 truncate">
											<span>{`${record.first_name} ${record.last_name}`}</span>
											{record.is_new && (
												<span className="bg-purple-200 text-[0.5rem] mb-3 px-1.5 py-[1px] rounded-full">
													חדש
												</span>
											)}
										</div>
										{userType && (
											<div
												className={`text-[0.55rem] ${
													userTypesColors[userType] ?? 'bg-teal-100'
												} px-1.5 py-[1px] rounded-full font-medium capitalize`}
											>
												{userTypesNames[userType] ?? userType}
											</div>
										)}
									</span>
								);
							}}
							label="שם מלא"
							textAlign="right"
							sortBy="first_name"
						/>
						<FunctionField
							render={(record: any) => (
								<div>
									<div className="text-xs mb-1">{record.email}</div>
									<div className="flex flex-row gap-1 items-center text-[0.55rem]">
										{!record.email_verified && (
											<span className="bg-red-300 px-2 py-0.5 rounded-full">לא מאומת</span>
										)}
										{record.push_token && (
											<span
												dir="ltr"
												className="bg-amber-100 px-1 py-0.5 rounded-full flex flex-row items-center gap-0.5"
											>
												<IoIosNotificationsOutline />
												<span>{'Push token'}</span>
											</span>
										)}
									</div>
								</div>
							)}
							label="מייל"
							textAlign="right"
							sortBy="email"
						/>

						<ReferenceField reference="company" source="company" label="חברה" textAlign="right">
							<TextField source="english_name" fontSize={14} />
						</ReferenceField>

						<FunctionField
							render={(record: any) => {
								const platformText =
									record.platform === 'android' ? (
										<img src="android.png" alt="android-logo" />
									) : record.platform === 'ios' ? (
										<img src="apple-logo.png" alt="apple-logo" />
									) : record.platform === 'web' ? (
										<img src="chrome.png" alt="chorme-logo" />
									) : (
										record.platform
									);
								return (
									<div className="text-xs flex flex-col items-center gap-0.5">
										<div className="flex flex-row justify-center">
											<span className="font-medium text-center capitalize">{platformText}</span>
										</div>
										<span>{moment(record.lastLoginTime).format('D/M/YY HH:mm')}</span>
									</div>
								);
							}}
							headerClassName="!text-center"
							label="התחברות אחרונה"
							textAlign="center"
							align="center"
							sortBy="lastLoginTime"
						/>

						<TextField source="version" label="גרסה" fontSize={14} textAlign="center" />
						<NumberField source="safe_space_count" label="מפגשי מרחב בטוח" textAlign="center" width={30} />
						{/* <FunctionField
							render={(record: any) => {
								const isSuperAdmin = localStorage.getItem('role') === 'superadmin';
								if (!isSuperAdmin) return null;
								return (
									<NumberField source="safe_space_count" label="מפגשי מרחב בטוח" textAlign="center" width={30} />
								);
							}}
							label="מפגשי מרחב בטוח"
							sortBy="safe_space_count"
						/> */}
					</Datagrid>
				)}
			</List>
		</>
	);
};

export default MemberList;
